/* cspell:disable */

import { envConfig } from '../../lib/env-config'
import type { TWorkspaceApp } from '../../types/apps'
import { AppCategoriesEnum, AppTypeEnum } from '../../types/workspace'
import { ATTFleetManagementModal } from './modals/att-fleet-management-modal'
import { BigRoadModal } from './modals/bigroad-modal'
import { CargoChiefModal } from './modals/cargo-chief-modal'
import { CloneOpsAIModal } from './modals/clone-ops-ai-modal'
import { ConvoyModal } from './modals/convoy-modal'
import { CoretexModal } from './modals/coretex-modal'
import { CreateAppAPIKeyModal } from './modals/create-app-api-key-modal'
import { EnableEmailRateConfirmationModal } from './modals/enable-email-rate-confirmation-modal'
import { EnableOAuthModal } from './modals/enable-oauth-modal'
import { ERoadModal } from './modals/eroad-modal'
import { ExpediteAllModal } from './modals/expedite-all-modal'
import { FleetLocateModal } from './modals/fleet-locate-modal'
import { FourKitesCarrierModal } from './modals/four-kites-carrier-modal'
import { GeoTabModal } from './modals/geo-tab-modal'
import { GreenscreensModal } from './modals/greenscreens-modal'
import { HighwayModal } from './modals/highway-modal'
import { HirschbachModal } from './modals/hirschbach-modal'
import { HutchModal } from './modals/hutch-modal'
import { ISAACModal } from './modals/isaac-modal'
import { McLeodTmsModal } from './modals/mcleod-tms-modal'
import { MondoTrackingModal } from './modals/mondo-tracking-modal'
import { MyCarrierPortalModal } from './modals/my-carrier-portal-modal'
import { OneStepGPSModal } from './modals/onestepgps-modal'
import { OrbcommModal } from './modals/orbcomm-modal'
import { Project44Modal } from './modals/project44-modal'
import { RMISModal } from './modals/rmis-modal'
import { SalesforceOAuthModal } from './modals/salesforce-oauth-modal'
import { SonarModal } from './modals/sonar-modal'
import { SunnybrookModal } from './modals/sunnybrook-modal'
import { TaiTMSModal } from './modals/tai-tms-modal'
import { TiveModal } from './modals/tive-modal'
import { TQLTraxModal } from './modals/tql-trax-modal'
import { TransporeonModal } from './modals/transporeon-modal'
import { TurvoModal } from './modals/turvo-modal'
import { US1NetworkTMSModal } from './modals/us1-network-tms-modal'
import { VerizonRevealModal } from './modals/verizon-reveal-modal'
import { VerizonTelogisModal } from './modals/verizon-telogis-modal'

export interface IAppDataBase {
  appName: string
  appType: AppTypeEnum
  available: boolean
  categories: AppCategoriesEnum[]
  darkImageURL: string
  description: string
  docs: string
  flag: string
  id: string
  isEditable?: boolean
  lightImageURL: string
  modal: (props: AppModalProps) => JSX.Element
  searchMetadata?: string[]
  scopes: string[]
  workspaceApp?: TWorkspaceApp
}

type TModalArray<T> = T extends ReadonlyArray<infer U> ? U : never
export type ModalElementType<T extends ReadonlyArray<unknown>> = TModalArray<T>

export type TAppData = ModalElementType<typeof apps> & {
  /**
   * Indicates if the app is enabled for this workspace
   */
  isEnabled: boolean
  /**
   * Indicates if the app is partially enabled (for apps like Expedite All that have sub-apps)
   */
  isPartiallyEnabled?: boolean
  /**
   * The modal for the app when clicking 'enable'
   */
  modal: (props: AppModalProps) => JSX.Element

  workspaceApp?: TWorkspaceApp

  /**
   * The workspaceID of the app returned from the workspaceApps reponse
   */
  workspaceID?: string
  /**
   * For Expedite All app, the workspace app for Booking
   */
  bookingWorkspaceApp?: TWorkspaceApp
  /**
   * For Expedite All app, the workspace app for Tracking
   */
  trackingWorkspaceApp?: TWorkspaceApp
}

export type AppModalProps = {
  app: IAppDataBase
  appType: AppTypeEnum
  isModalEditable?: boolean
  isOpen: boolean
  /**
   * Used if you are using a modal in which you can add a subttitle.
   * The CreateAPPAPIKeyModal uses this.
   */
  modalSubtitle?: string
  /**
   * Used if you are using a modal in which you can add a title.
   * The CreateAPPAPIKeyModal uses this.
   */
  modalTitle?: string
  /**
   * If a modal requires a specific OAuth URL, you can pass it here.
   */
  oAuthBaseURL?: string
  /**
   * If a modal requires a specific OAuth redirect URI suffix, you can pass it here.
   * This is used in the redirect_uri as such:
   * `${config.app.BASE_OAUTH_CALLBACK_URL}/${oAuthRedirectURISuffix}`
   */
  oAuthRedirectURISuffix?: string
  /**
   * If a modal requires a specific OAuth scope, you can pass it here.
   * This will add it to the scope query parameter in the OAuth URL:
   * `${oAuthScope ? `&scope=${oAuthScope}` : ''}`
   */
  oAuthScope?: string
  onClose: () => void
  setModalEditable?: (value: boolean) => void
  workspaceApp?: TWorkspaceApp
  /**
   * For Expedite All app, pass both workspace apps (Booking and Tracking)
   */
  bookingWorkspaceApp?: TWorkspaceApp
  trackingWorkspaceApp?: TWorkspaceApp
}

/**
 * This is the array of apps that are displayed in the apps page.
 */
export const apps = [
  {
    appName: 'ATT Fleet Management',
    appType: AppTypeEnum.ATTFleetManagement,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/att-fleet-management-dark.svg',
    description:
      'Carriers, connect your ATT Fleet Management account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845749-at-t-fleet-management',
    flag: 'coming-soon',
    id: '7.1',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/att-fleet-management-light.svg',
    searchMetadata: ['GeoTab'],
    modal: ATTFleetManagementModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Convoy For Brokers - Tracking',
    appType: AppTypeEnum.ConvoyTracking,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/convoy-dark.svg',
    description:
      'Convoy for Brokers customers can get tracking data from Convoy and update the load in Chain and your TMS.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/convoy',
    flag: 'coming-soon',
    id: '4.3',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/convoy-light.svg',
    modal: ConvoyModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.OneStepGPS,
    appType: AppTypeEnum.OneStepGPS,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/onestepgps-dark.png',
    description:
      'Carriers, connect your OneStepGPS account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845815-onestep-gps',
    flag: 'coming-soon',
    id: '4.7',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/onestepgps-light.png',
    modal: OneStepGPSModal,
    searchMetadata: ['One Step', 'One', 'GPS'],
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Orbcomm Tracking',
    appType: AppTypeEnum.Orbcomm,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/orbcomm-dark.svg',
    description:
      'Carriers, connect your Orbcomm account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/orbcomm',
    flag: 'coming-soon',
    id: '4.4',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/orbcomm-light.svg',
    modal: OrbcommModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.Highway,
    appType: AppTypeEnum.Highway,
    available: true,
    categories: [AppCategoriesEnum.CarrierVetting],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/highway-dark.svg',
    description: 'Enable Highway to verify carrier identity and safety information.',
    docs: '',
    flag: 'coming-soon',
    id: '14',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/highway-light.svg',
    modal: HighwayModal,
    oAuthBaseURL:
      envConfig.envStage === 'prod'
        ? 'https://highway.com/onboarding/oauth/authorizations'
        : 'https://staging.highway.com/onboarding/oauth/authorizations',
    oAuthRedirectURISuffix: 'highway',
    scopes: ['Carrier Vetting']
  },
  {
    appName: AppTypeEnum.MyCarrierPortal,
    appType: AppTypeEnum.MyCarrierPortal,
    available: true,
    categories: [AppCategoriesEnum.CarrierVetting],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/my-carrier-portal-dark.svg',
    description: 'Enable My Carrier Portal to verify carrier identity and safety information.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/my-carrier-portal',
    flag: 'coming-soon',
    id: '8',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/my-carrier-portal-light.svg',
    modal: MyCarrierPortalModal,
    scopes: ['Carrier Vetting']
  },
  {
    appName: AppTypeEnum.RMIS,
    appType: AppTypeEnum.RMIS,
    available: true,
    categories: [AppCategoriesEnum.CarrierVetting],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/rmis-dark.svg',
    description: 'Enable RMIS to verify carrier identity and safety information.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/rmis',
    flag: 'coming-soon',
    id: '9',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/rmis-light.svg',
    modal: RMISModal,
    scopes: ['Carrier Vetting']
  },
  {
    appName: AppTypeEnum.CargoChief,
    appType: AppTypeEnum.CargoChief,
    available: true,
    categories: [AppCategoriesEnum.Rating],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/cargo-chief-dark.svg',
    description: 'Connect your CargoChief account to lane rate prediction data automatically integrated to your posts.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/cargo-chief',
    flag: 'coming-soon',
    id: '9.99',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/cargo-chief-light.svg',
    modal: CargoChiefModal,
    scopes: ['Booking', 'Rating']
  },
  {
    appName: AppTypeEnum.Greenscreens,
    appType: AppTypeEnum.Greenscreens,
    available: true,
    categories: [AppCategoriesEnum.Rating],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/greenscreens-dark.svg',
    description:
      'Connect your Greenscreens.ai account to lane rate prediction data automatically integrated to your posts.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/greenscreens',
    flag: 'coming-soon',
    id: '10',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/greenscreens-light.svg',
    modal: GreenscreensModal,
    scopes: ['Booking', 'Rating']
  },
  {
    appName: AppTypeEnum.Sonar,
    appType: AppTypeEnum.Sonar,
    available: true,
    categories: [AppCategoriesEnum.Rating],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/freightwaves-sonar-dark.svg',
    description:
      'Connect your Freightwaves Sonar rate data to automatically integrated to your posts in your workspace.',
    docs: '',
    flag: 'coming-soon',
    id: '10.5',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/freightwaves-sonar-light.svg',
    modal: SonarModal,
    scopes: ['Booking', 'Rating']
  },
  {
    appName: AppTypeEnum.Tai,
    appType: AppTypeEnum.Tai,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/tai-tms-dark.svg',
    description:
      'Integrate Tai TMS to sync booked loads for visibility and collaboration, and your available loads to post to your network.',
    docs: 'https://help.trychain.com/en/collections/11885015-tai-tms',
    flag: 'coming-soon',
    id: '13',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/tai-tms-light.svg',
    modal: TaiTMSModal,
    modalTitle: 'Enable Tai integration for Chaine',
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: AppTypeEnum.Turvo,
    appType: AppTypeEnum.Turvo,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/turvo-dark.svg',
    description:
      'Integrate Turvo to sync booked loads for visibility and collaboration, and your available loads to post to your network.',
    docs: 'https://help.trychain.com/en/collections/11885015-turvo-tms',
    flag: 'coming-soon',
    id: '13.1',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/turvo-light.svg',
    modal: TurvoModal,
    modalTitle: 'Enable Turvo integration for Chaine',
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: AppTypeEnum.Sunnybrook,
    appType: AppTypeEnum.Sunnybrook,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/sunnybrook-dark.svg',
    description:
      'Integrate Sunnybrook to sync booked loads for visibility and collaboration, and your available loads to post to your network.',
    docs: 'https://help.trychain.com/en/collections/11885015-sunnybrook-tms',
    flag: 'coming-soon',
    id: '13.1',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/sunnybrook-light.svg',
    modal: SunnybrookModal,
    modalTitle: 'Enable Sunnybrook integration for Chaine',
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: AppTypeEnum.Mercurygate,
    appType: AppTypeEnum.Mercurygate,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/mercurygate-dark.svg',
    description:
      'Integrate Mercurygate TMS to sync booked loads for visibility and collaboration, and your available loads to post to your network.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/mercurygate',
    flag: 'coming-soon',
    id: '14.1',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/mercurygate-light.svg',
    modal: CreateAppAPIKeyModal,
    modalSubtitle:
      'To enable Mercurygate for Chaine, you will need to create an API key and request Mercurygate to use it in the Authentication headers. Refer to the docs.',
    modalTitle: 'Enable Mercurygate integration for Chaine',
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: AppTypeEnum.ThreeGTMS,
    appType: AppTypeEnum.ThreeGTMS,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/3gtms-dark.svg',
    description:
      'Integrate 3GTMS to sync booked loads for visibility and collaboration, and your available loads to post to your network.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/3gtms',
    flag: 'coming-soon',
    id: '14.12',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/3gtms-light.svg',
    modal: CreateAppAPIKeyModal,
    modalSubtitle:
      'To enable 3GTMS for Chaine, you will need to create an API key and request Mercurygate to use it in the Authentication headers. Refer to the docs.',
    modalTitle: 'Enable 3GTMS integration for Chaine',
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: 'Clone Ops',
    appType: AppTypeEnum.CloneOps,
    available: true,
    categories: [AppCategoriesEnum.AICommunication],
    darkImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/clone-ops-dark.png',
    description:
      'Integrate CloneOps for AI-powered voice communication to automate routine tasks and escalate complex interactions.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/clone-ops',
    flag: 'coming-soon',
    id: '17',
    lightImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/clone-ops-light.png',
    modal: CloneOpsAIModal,
    scopes: ['Communication']
  },
  {
    appName: 'Revenova (Production)',
    appType: AppTypeEnum.RevenovaProduction,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/salesforce-dark.svg',
    description:
      'Integrate Revenova production to sync booked loads for visibility and collaboration, and your available loads to post to your network.',
    docs: 'https://help.trychain.com/en/collections/11885047-revenova-tms',
    flag: 'coming-soon',
    id: '14.13',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/salesforce-light.svg',
    modal: SalesforceOAuthModal,
    scopes: ['Truckload Visibility', 'Available Load Postings']
  },
  {
    appName: 'Revenova (Sandbox)',
    appType: AppTypeEnum.RevenovaSandbox,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/salesforce-dark.svg',
    description: 'Integrate Chaine to Revenovas sandbox to test out the integration in yoru Sandbox.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/revenova',
    flag: 'coming-soon',
    id: '14.14',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/salesforce-light.svg',
    modal: SalesforceOAuthModal,
    scopes: ['Truckload Visibility', 'Available Load Postings']
  },
  {
    appName: AppTypeEnum.Samsara,
    appType: AppTypeEnum.Samsara,
    available: true,
    categories: [AppCategoriesEnum.ELD, AppCategoriesEnum.TrailerTracking],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/samsara-dark.svg',
    description:
      'Carriers, connect your Samsara account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/samsara',
    flag: 'coming-soon',
    id: '2',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/samsara-light.svg',
    modal: EnableOAuthModal,
    oAuthBaseURL: 'https://api.samsara.com/oauth2/authorize',
    oAuthRedirectURISuffix: 'samsara',
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.Project44,
    appType: AppTypeEnum.Project44,
    available: true,
    categories: [AppCategoriesEnum.VisibilityProvider],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/p44-dark.svg',
    description:
      'Connect freight visibility platform, project44, to enable visibility on your loads or to share with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/category/project44',
    flag: 'coming-soon',
    id: '3',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/p44-light.svg',
    modal: Project44Modal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'FourKites Carrier',
    appType: AppTypeEnum.FourKitesCarrier,
    available: true,
    categories: [AppCategoriesEnum.VisibilityProvider],
    darkImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/four-kites-carrier-dark.svg',
    description: 'Connect with FourKites to send the details to customers for tracking',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/four-kites',
    // not sure
    flag: 'coming-soon',
    id: '4.3',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/four-kites-carrier-light.svg',
    modal: FourKitesCarrierModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Transporeon',
    appType: AppTypeEnum.Transporeon,
    available: true,
    categories: [AppCategoriesEnum.VisibilityProvider],
    darkImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/transporeon-dark.png',
    description: 'Connect with Transporeon to send the details to customers for tracking',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/transporeon',
    // not sure
    flag: 'coming-soon',
    id: '4.43',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/transporeon-light.png',
    modal: TransporeonModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'McLeod',
    appType: AppTypeEnum.McLeod,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/mcleod-dark.svg',
    description: 'Enable the McLeod integration to sync booked loads for visibility and collaboration.',
    docs: 'https://help.trychain.com/en/collections/11885104-mcleod-tms',
    // not sure
    flag: 'coming-soon',
    id: '4.31',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/mcleod-light.svg',
    modal: McLeodTmsModal,
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: AppTypeEnum.FleetLocate,
    appType: AppTypeEnum.FleetLocate,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/fleetlocate-dark.svg',
    description:
      'Carriers, connect your FleetLocate (by Spireon) account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845818-fleetlocate-by-spireon',
    flag: 'coming-soon',
    id: '4.2',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/fleetlocate-light.svg',
    modal: FleetLocateModal,
    searchMetadata: ['FleetLocate', 'Spireon'],
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.Hutch,
    appType: AppTypeEnum.Hutch,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/hutch-dark.png',
    description:
      'Carriers, connect your Hutch account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845810-hutch-systems',
    flag: 'coming-soon',
    id: '4.5',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/hutch-light.png',
    modal: HutchModal,
    searchMetadata: ['Hutch', 'Systems', 'Hutch Systems'],
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.Coretex,
    appType: AppTypeEnum.Coretex,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/coretex-dark.png',
    description:
      'Carriers, connect your Coretex (by Spireon) account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845823-coretex-by-eroad',
    flag: 'coming-soon',
    id: '4.2',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/coretex-light.png',
    modal: CoretexModal,
    searchMetadata: ['Coretex', 'EROAD'],
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.ERoad,
    appType: AppTypeEnum.ERoad,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/eroad-dark.svg',
    description:
      'Carriers, connect your ERoad account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845832-eroad',
    flag: 'coming-soon',
    id: '4.4',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/eroad-light.svg',
    modal: ERoadModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Expedite All',
    appType: AppTypeEnum.ExpediteAll,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/expedite-all-dark.svg',
    description:
      'Integrate Expedite All to sync booked loads for visibility and collaboration, and enable tracking capabilities.',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/expedite-all',
    flag: 'coming-soon',
    id: '13.2',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/expedite-all-light.svg',
    modal: ExpediteAllModal,
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: AppTypeEnum.Motive,
    appType: AppTypeEnum.Motive,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/motive-dark.svg',
    description:
      'Carriers, connect your Motive (formerly KeepTruckin) account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/motive',
    flag: 'coming-soon',
    id: '5',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/motive-light.svg',
    modal: EnableOAuthModal,
    oAuthBaseURL: 'https://gomotive.com/oauth/authorize',
    oAuthRedirectURISuffix: 'motive',
    oAuthScope:
      'vehicles.read eld_devices.read locations.vehicle_locations_list locations.vehicle_locations_single companies.read assets.read',
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.Hirschbach,
    appType: AppTypeEnum.Hirschbach,
    available: true,
    categories: [AppCategoriesEnum.ThirdPartyCarrierTrackingAPI],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/hirschbach-dark.svg',
    description:
      'Carriers, connect your Hirschbach account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/hirschbach',
    flag: 'coming-soon',
    id: '4.2',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/hirschbach-light.svg',
    modal: HirschbachModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.ISAAC,
    appType: AppTypeEnum.ISAAC,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/isaac-dark.svg',
    description:
      'Carriers, connect your ISAAC account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845782-issac-instruments',
    flag: 'coming-soon',
    id: '4.2',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/isaac-light.svg',
    modal: ISAACModal,
    searchMetadata: ['ISAAC', 'ISAAC Instruments'],
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.TQLTrax,
    appType: AppTypeEnum.TQLTrax,
    available: true,
    categories: [AppCategoriesEnum.ThirdPartyCarrierTrackingAPI],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/tql-trax-dark.svg',
    description:
      'Carriers, connect your TQL Trax account to add visibility on your loads or securely share them with the companies you work with.',
    // Need to modify this
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/tql-trax',
    flag: 'coming-soon',
    id: '4.2',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/tql-trax-light.svg',
    modal: TQLTraxModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.Orbcomm,
    appType: AppTypeEnum.Orbcomm,
    available: true,
    categories: [AppCategoriesEnum.TrailerTracking, AppCategoriesEnum.TemperatureMonitoring],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/orbcomm-dark.svg',
    description:
      'Carriers, connect your Orbcomm trailer tracking account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/orbcomm',
    flag: 'coming-soon',
    id: '6',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/orbcomm-light.svg',
    modal: CreateAppAPIKeyModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.GeoTab,
    appType: AppTypeEnum.GeoTab,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/geotab-dark.svg',
    description:
      'Carriers, connect your GeoTab asset tracking account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845771-geotab',
    flag: 'coming-soon',
    id: '7',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/geotab-light.svg',
    modal: GeoTabModal,
    searchMetadata: ['GeoTab', 'AT&T Fleet Management', 'ATT'],
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.BigRoad,
    appType: AppTypeEnum.BigRoad,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/bigroad-dark.svg',
    description:
      'Carriers, connect your BigRoad asset tracking account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/bigroad',
    flag: 'coming-soon',
    id: '11',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/bigroad-light.svg',
    modal: BigRoadModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Rate confirmation PDF to Chaine',
    appType: AppTypeEnum.EmailRateConfirmation,
    available: true,
    categories: [],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/email-rate-con-dark.svg',
    description:
      'You can autoforward the rate confirmation pdfs to a unique Chaine email we will provide to automatically create the loads in Chaine. This can be used if your TMS is not integrated with Chaine',
    docs: 'https://handbook.chaineapp.com/product-guide/apps/email-rate-con',
    flag: 'coming-soon',
    id: '12',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/email-rate-con-light.svg',
    modal: EnableEmailRateConfirmationModal,
    scopes: []
  },
  {
    appName: AppTypeEnum.Tive,
    appType: AppTypeEnum.Tive,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/tive-dark.svg',
    description:
      'Carriers, connect your Tive account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/tive',
    flag: 'coming-soon',
    id: '4.3',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/tive-light.svg',
    modal: TiveModal,
    scopes: ['Truckload Visibility']
  },
  {
    appName: AppTypeEnum.Txt,
    appType: AppTypeEnum.Txt,
    available: false,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/txt-dark.svg',
    description:
      'Carriers, connect your TXT Solutions asset tracking account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/txt-eld',
    flag: 'coming-soon',
    id: '16',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/txt-light.svg',
    modal: EnableOAuthModal,
    oAuthBaseURL: 'https://api.samsara.com/oauth2/authorize',
    oAuthRedirectURISuffix: 'samsara',
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Truckman TMS',
    appType: AppTypeEnum.US1NetworkTMS,
    available: true,
    categories: [AppCategoriesEnum.TMS],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/truckman-dark.svg',
    description: "Integrate US1 Network's Truckman TMS to sync booked loads for visibility and collaboration.",
    docs: 'https://help.trychain.com/en/',
    flag: 'coming-soon',
    id: '14.2',
    isEditable: true,
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/truckman-light.svg',
    modal: US1NetworkTMSModal,
    scopes: ['Truckload Visibility', 'Booking']
  },
  {
    appName: 'Verizon-Telogis',
    appType: AppTypeEnum.VerizonConnectFleetTelogis,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL:
      'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/verizon-connect-telogis-dark.png',
    description:
      'Carriers, connect your Verizon-Telogis account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845801-verizon-connect-fleet-telogis',
    flag: 'coming-soon',
    id: '4',
    lightImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/verizon-connect-telogis.png',
    modal: VerizonTelogisModal,
    searchMetadata: [
      'Verizon',
      'Telogis',
      'Verizon Connect',
      'Connect',
      'Verizon Fleet',
      'Verizon Connect Fleet Telogis'
    ],
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Verizon-Reveal',
    appType: AppTypeEnum.VerizonConnectReveal,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/verizon-connect-reveal-dark.png',
    description:
      'Carriers, connect your Verizon-Reveal account to add visibility on your loads or securely share them with the companies you work with.',
    docs: 'https://help.trychain.com/en/articles/12845839-verizon-connect-reveal',
    flag: 'coming-soon',
    id: '4.6',
    lightImageURL:
      'https://chaine-images.s3.us-east-1.amazonaws.com/product-app-images/verizon-connect-reveal-light.png',
    modal: VerizonRevealModal,
    searchMetadata: ['Verizon', 'Reveal', 'Verizon Connect', 'Connect', 'Verizon Fleet', 'Verizon Connect Reveal'],
    scopes: ['Truckload Visibility']
  },
  {
    appName: 'Mondo Tracking',
    appType: AppTypeEnum.MondoTracking,
    available: true,
    categories: [AppCategoriesEnum.ELD],
    darkImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/mondo-tracking-dark.png',
    description:
      'Mondo Tracking provides GPS tracking solutions for fleets with the backing of Apollo ELD. You can connect your Mondo Tracking account to add visibility on your loads.',
    docs: 'https://help.trychain.com/en/articles/12845858-mondo-tracking',
    flag: 'coming-soon',
    id: '15',
    lightImageURL: 'https://chaine-images.s3.amazonaws.com/product-app-images/mondo-tracking-light.png',
    modal: MondoTrackingModal,
    searchMetadata: ['Mondo Tracking', 'Apollo ELD'],
    scopes: ['Truckload Visibility']
  }
]
